import pino from 'pino';

/**
 * Logger configuration using pino for structured logging
 * Only active in development environment, silent in production
 */

// Create logger configuration based on environment
const isDevelopment = process.env.NODE_ENV === 'development';

const pinoLogger = pino({
  level: isDevelopment ? 'debug' : 'silent',
  transport: isDevelopment ? {
    target: 'pino-pretty',
    options: {
      colorize: true,
      translateTime: 'SYS:standard',
      ignore: 'pid,hostname',
    },
  } : undefined,
  // In production, we could potentially log to a file or external service
  // For now, we're making it completely silent in production
});

/**
 * Logger utility with multiple log levels
 * Only outputs in development environment to avoid production noise
 * 
 * @example
 * logger.info('User logged in', { userId: '123' });
 * logger.error('Database connection failed', error);
 * logger.debug('Processing request', { method: 'POST', path: '/api/users' });
 */
export const logger = {
  /**
   * Log debug information (development only)
   * @param message - Log message
   * @param args - Additional context data
   */
  debug: (message: string, ...args: any[]) => {
    if (isDevelopment) {
      pinoLogger.debug({ args }, message);
    }
  },
  /**
   * Log informational messages (development only)
   * @param message - Log message
   * @param args - Additional context data
   */
  info: (message: string, ...args: any[]) => {
    if (isDevelopment) {
      pinoLogger.info({ args }, message);
    }
  },
  /**
   * Log warning messages (development only)
   * @param message - Log message
   * @param args - Additional context data
   */
  warn: (message: string, ...args: any[]) => {
    if (isDevelopment) {
      pinoLogger.warn({ args }, message);
    }
  },
  /**
   * Log error messages (development only)
   * @param message - Log message
   * @param args - Additional context data (errors, stack traces, etc.)
   */
  error: (message: string, ...args: any[]) => {
    if (isDevelopment) {
      pinoLogger.error({ args }, message);
    }
  },
};

export default logger;